import { create } from 'zustand';
import flatMap from 'lodash/flatMap';
import find from 'lodash/find';
import { PrismUI, builderDispatch, WebUI } from '../../_utils/ui';
import fetchImage from '../../_utils/fetch-image';
import IU from '../../_utils/img/img-utils';
import TemplateCache from '../templates/template-cache';
import TemplateStore from '../templates/template-store';
import Utils from '../utils/utils';
import PrismJSON from '../prism/prism-json-encoder';
import { getCurrentSection } from '../trays/favourites-tray-helpers';
import {
  isCalendarEventPhotoSlot,
  isOptionalSlot,
  isClonedSlot,
} from '../utils/special-slots';
import genericHelpers from '../../_utils/generic-helpers';
import { useTemplateSelectStore } from './useTemplateStore';
import {
  isCanvas,
  isCalendar,
  isMultiPageStandardProduct,
  isMultiPageCardProduct,
} from '../trays/tray-determiner';
import { autofillOnImageUploadNormal } from '../utils/autofill';
import DpiThreshold from '../utils/dpi-threshold';

const { urlMunge } = IU.imgHelpers;

export const usePrismItemStore = create((set, get) => ({
  prismItem: {
    id: '',
    data: null,
    currentPage: 0,
    pageCount: 0,
    activeSaves: {},
    imageList: [],
    loadActive: false,
    _fireSaves: {},
  },
  prismItemLoaded: false,
  imagesUploadProgress: {
    current: 0,
    totalFiles: 0,
  },
  failedImagesUploaded: [],
  trackProgress: () => {
    const { current, totalFiles } = get().imagesUploadProgress;

    if (current === totalFiles) {
      builderDispatch.dispatch({
        type: WebUI.UPLOAD_DONE,
        number_of_files: totalFiles,
      });
    } else {
      set({
        imagesUploadProgress: {
          current: current + 1,
          totalFiles: totalFiles,
        },
      });
    }
  },
  isLayflat: () => {
    return flatMap(
      get().prismItem.pages,
      page => (page.template || {}).tags || []
    ).includes('layflat');
  },
  minPagesReached: () => {
    return (
      get().prismItem.product &&
      get().prismItem.pageCount <= get().prismItem.product.minPages
    );
  },
  maxPagesReached: () => {
    return (
      get().prismItem.product &&
      get().prismItem.pageCount >= get().prismItem.product.maxPages
    );
  },
  selectTheme: () => {},
  listPages: () => {
    const state = get();
    if (!state || !state.prismItem) return [];
    return Object.keys(state.prismItem.pages || {}).map(
      p => state.prismItem.pages[p]
    );
  },
  totalNumberPhotoSlots: async () => {
    try {
      const state = get();
      if (!state || !state.prismItem || !state.prismItem.pages) return 0;

      const pages = state.prismItem.pageCount;
      if (!pages) return 0;

      const pageTemplates = await Promise.all(
        Array.from({ length: pages }, (_, i) => state.pageWithTemplate(i))
      );

      return pageTemplates.reduce((total, { template }) => {
        if (!template?.photo_slots) return total;
        
        const validSlots = Object.entries(template.photo_slots).filter(([_, slot]) => {
          if (!slot || typeof slot !== 'object') {
            return false;
          }
          const isOptionalBg = slot.id === 'optional-bg';
          return !isOptionalBg;
        });
        return total + validSlots.length;
      }, 0);
    } catch (error) {
      console.error('Error calculating total slots:', error);
      return 0;
    }
  },
  onlyHasOnePhotoSlot: async () => {
    const total = await get().totalNumberPhotoSlots();
    return total === 1;
  },
  fireSave: (view, callback = null) => {
    view = view || activeView;

    const { pageIndex } = view;
    const data = view.getData(true);

    get().prismItem.pages[pageIndex] = {
      ...get().prismItem.pages[pageIndex],
      data,
    };

    get().prismItem._fireSaves = get().prismItem._fireSaves || {};
    get().prismItem._fireSaves[pageIndex] =
      get().prismItem._fireSaves[pageIndex] || [];
    get().prismItem._fireSaves[pageIndex].unshift(view);

    if (get().prismItem._fireSaves[pageIndex].length > 1) {
      return;
    }

    get()
      .savePage(pageIndex, data)
      .then(() => {
        const nextView = get().prismItem._fireSaves[pageIndex].shift();
        const thereWasMore = get().prismItem._fireSaves[pageIndex].length > 0;

        get().prismItem._fireSaves[pageIndex] = [];

        if (thereWasMore) {
          get().fireSave(nextView);
        } else {
          if (callback) callback();
        }
      });
  },
  setDayEventsSlots: async () => {
    const pagesWithDailyTextSlots = [];

    for (let i = 0; i < get().prismItem.pageCount; i++) {
      const { page, template } = await get().pageWithTemplate(i);
      const pageWithDailyTextSlots = {};

      for (const slotId in template.text_slots) {
        if (/text-\d\d\d\d-\d\d-\d\d/gi.exec(slotId)) {
          let slot;

          for (let x = 0; x < page.slots.length; x++) {
            if (page.slots[x].id === slotId) {
              slot = page.slots[x];
              break;
            }
          }

          pageWithDailyTextSlots[slotId] = !!(slot && slot.image);
        }
      }

      if (Object.keys(pageWithDailyTextSlots).length > 0) {
        pagesWithDailyTextSlots.push(pageWithDailyTextSlots);
      }
    }

    return pagesWithDailyTextSlots;
  },
  refreshImages: async () => {
    const prismItemId = get().prismItem.id;

    if (!prismItemId) return;

    const url = `/m/i/${prismItemId}`;

    const data = await fetch(url, {
      credentials: 'same-origin',
    });

    return new Promise(resolve => {
      const url = `/m/i/${prismItemId}`;

      fetch(url, {
        credentials: 'same-origin',
      })
        .then(response => response.json())
        .then(data => {
          set(({ prismItem }) => ({
            prismItem: { ...prismItem, imageList: data },
          }));
          return data;
        })
        .then(data => resolve(data));
    });
  },
  hasSingleImageSlot: () => {
    if (!window.activeView) {
      return false;
    }

    const imageSlots = window.activeView.slots.filter(slot => slot.isImage);

    return imageSlots.length === 1;
  },
  autoFillImageUponUpload: () => {
    const autoFillCriteria =
      get().prismItem.imageList.length &&
      get().hasSingleImageSlot() &&
      !isMultiPageStandardProduct();

    if (autoFillCriteria) {
      if (isMultiPageCardProduct() || isCalendar()) {
        const firstImageOnly = get().prismItem.imageList[0];

        const imageList = [firstImageOnly];
        autofillOnImageUploadNormal(imageList);
      } else {
        const imageList = get().prismItem.imageList;
        autofillOnImageUploadNormal(imageList);
      }
    }
  },
  addNewImagesToItem: imagesList => {
    let changed = false;

    for (let i = 0; i < imagesList.length; i++) {
      const slotId = activeView.findFirstAvailableImageSlot();

      // break if no slot is available
      if (!slotId) break;

      // set the image in the available slot
      const img = fetchImage(urlMunge(imagesList[i].url), 'use-credentials');
      activeView.setImageById(slotId, img, {
        image: imagesList[i].id,
      });
      changed = true;
    }

    // If a change happened, save
    if (changed) activeView.do_save();
  },
  checkSlots: () => {
    let filledImageSlot = false;
    let emptyImageSlot = false;
    let unchangedDefaultText = false;
    let spineTextSlotPresent = false;
    let spineTextPresent = false;
    let lowDpiImage = false;
    let emptyPage = false;
    const pagePromises = Object.entries(get().prismItem.pages)
      .filter(([key, _]) => isFinite(key))
      .map(([_, page]) => evaluatePage(page));

    // when ALL the pagePromises are resolved
    return Promise.all(pagePromises).then(() => ({
      filledImageSlot,
      emptyImageSlot,
      unchangedDefaultText,
      spineTextSlotPresent,
      spineTextPresent,
      lowDpiImage,
      emptyPage,
    }));

    function isSkippableSlot(slot) {
      return [isCalendarEventPhotoSlot, isOptionalSlot, isClonedSlot].some(
        check => check(slot.id)
      );
    }

    function evaluatePage(page) {
      if (
        [
          filledImageSlot,
          emptyImageSlot,
          unchangedDefaultText,
          spineTextSlotPresent,
          spineTextPresent,
          lowDpiImage,
          emptyPage,
        ].every(Boolean)
      ) {
        // there's no need checking anything if we already know all items are true
        return Promise.resolve();
      }

      return new Promise(resolve => {
        TemplateCache.get(page.templateID, template => {
          if (!emptyPage && page.slots.length < 1) {
            emptyPage = true;
          }

          // Check all slots (predefined template slots as well as user added image slots) to see if
          // any of them have been filled
          if (!filledImageSlot || !lowDpiImage ) {
            for (const slot of page.slots) {
              // Some special slots can be skipped as we don't want to include them in the checks, we do need to check

              let isFrontOrBack = false;
              if (slot.id === 'optional-front' || slot.id === 'optional-back') {
                isFrontOrBack = true;
              }

              if (isSkippableSlot(slot) && isFrontOrBack == false) {
                continue;
              }

              if (!slot.data.layout) {
                const coord = get().listPages()?.[page?.index]
                  ?.template?.photo_slots?.[slot?.id]?.['pixel-coords'] ?? [
                  0, 0, 1, 1,
                ];

                const [x, y, w, h] = coord;
                slot.data.layout = { x, y, w, h };
              } else {
                console.warn(
                  'Template layout is missing for page index:',
                  page?.index,
                  'or slot id:',
                  slot?.id,
                );
              }

              if (slot.image && slot.data.layout) {
                filledImageSlot = true;

                const {
                  layout: { w, h },
                  angle,
                  imgId,
                } = slot.data;
                const photoData = get().prismItem.imageList.find(
                  image => image.id === Number(imgId)
                );
                if (!photoData) return;

                if (lowDpiImage === false) {
                  if (slot.data.lowDpi) {
                    lowDpiImage = true;
                  } else {
                    const dpi = genericHelpers.getImageOutputDpiWithSlotData(
                      slot.data,
                      photoData
                    );
                    lowDpiImage = slot.data.lowDpi = new DpiThreshold(
                      dpi
                    ).isTooLow;
                  }
                }
              }

              // Only check predefined slots to see if any of them are empty, stackable slots are
              // allowed to be empty.
              if (template.photo_slots[slot.id] && !slot.image) {
                emptyImageSlot = true;
              }
            }
          }

          // Only check predefined text slots to see if any of them still contain their default text
          // value
          if (!unchangedDefaultText && Object.keys(template.text_slots).length) {
            for (const slotId in template.text_slots) {
              const slot = find(page.slots, { id: slotId })
                ? find(page.slots, { id: slotId })
                : find(page.slots, { id: 'override-text-' + slotId });
              
              if (slot && isSkippableSlot(slot)) {
                return;
              }
      
              const templateSlot = template.text_slots[slotId];
              const slotsExist = slot && templateSlot ? true : false;

              if (
                slotsExist &&
                templateSlot.default.localeCompare(slot.data.string, undefined, {sensitivity: 'base',}) === 0
              ) {
                unchangedDefaultText = true;
              };

              break;
            }
          }
          if (!spineTextSlotPresent && 'spine-text' in template.text_slots) {
            spineTextSlotPresent = true;
          
            const pages = Object.values(prismItem.pages);
          
            spineTextPresent = pages.some(page =>
              Array.isArray(page.slots) && page.slots.some(slot =>
                slot.id === 'spine-text' && (slot.data?.string?.length > 0)
              )
            );
          }
          
          
          resolve();
        });
      });
    }
  },



  populateItemsImagesAfterUpload: imageData => {
    if (imageData.length === 0) return;

    const newImageIds = imageData.map(i => i.id);
    const newImages = get().prismItem.imageList.filter(
      i => newImageIds.indexOf(i.id) >= 0
    );

    get().addNewImagesToItem(newImages);
  },
  // Add images from the list to empty slots in a collage project
  addNewImagesToItem: imagesList => {
    let changed = false;

    for (let i = 0; i < imagesList.length; i++) {
      const slotId = activeView.findFirstAvailableImageSlot();

      // break if no slot is available
      if (!slotId) break;

      // set the image in the available slot
      const img = fetchImage(urlMunge(imagesList[i].url), 'use-credentials');
      activeView.setImageById(slotId, img, {
        image: imagesList[i].id,
      });
      changed = true;
    }

    // If a change happened, save
    if (changed) activeView.do_save();
  },
  findImage: ref => {
    for (let i = 0; i < get().prismItem.imageList.length; i++) {
      const image = get().prismItem.imageList[i];

      if (ref.image == image.id) {
        let { filter } = ref.data;
        if (!filter) filter = 'colour';
        ref.data.url = image.urls[filter];
      }
    }
    return ref;
  },
  usedImages: () => {
    const used = [];
    for (let i = 0; i < get().prismItem.pageCount; i++) {
      if (get().prismItem.pages[i] && get().prismItem.pages[i].slots) {
        const pg = get().prismItem.pages[i];
        for (let j = 0; j < get().prismItem.pages[i].slots.length; j++) {
          const { image } = pg.slots[j];
          if (image && used.indexOf(image) === -1) {
            used.push(image);
          }
        }
      }
    }
    return used;
  },
  pageWithTemplate: index => {
    return new Promise((resolve, reject) => {
      get().getPage(index, page => {
        TemplateCache.get(
          page.templateID,
          template => {
            resolve({ page, template });
          },
          () => {
            reject(new Error(`Failed to load template ${page.templateID}`));
          }
        );
      });
    });
  },
  getPage: (index, callback) => {
    callback(get().prismItem.pages[index]);
  },
  savePage: (index, data, options = {}) => {
    if (typeof index === 'undefined') {
      return Promise.resolve();
    }

    const saveID = `save-${parseInt(
      Math.random() * 1000,
      10
    )}-${index}-${new Date().getTime()}`;

    $(window).trigger('prism-page-save-start', { index, saveID });

    if (builderDispatch) {
      builderDispatch.delayedDispatch({
        type: PrismUI.SAVE_START,
        saveID,
        pageIndex: index,
        prismItem: get().prismItem,
      });
    }

    if (wkData && wkData.values) {
      const overlays = {};

      for (const { id, name } of wkData.vars) {
        if (wkData.values[id]) {
          overlays[name] = wkData.values[id];
        }
      }

      data.overlays = overlays;
    }

    const formData = new FormData();
    formData.append('data', PrismJSON.encode(data));

    return fetch(Utils.pageURL(get().prismItem, index), {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
    })
      .then(response => {
        if (!response.ok) {
          throw Error(response.statusText);
        }
        return response;
      })
      .then(response => response.json())
      .then(data => {
        const { doNotSave = false } = options;

        if (!doNotSave) {
          const pages = get().prismItem.pages;
          pages[index] = data;
          set(({ prismItem }) => ({
            prismItem: { ...prismItem, pages: pages },
          }));
        }

        $(window).trigger('prism-page-save-done', { index, saveID });

        if (builderDispatch) {
          builderDispatch.delayedDispatch({
            type: PrismUI.SAVE_DONE,
            saveID,
            pageIndex: index,
            prismItem: get().prismItem,
          });
        }
      })
      .catch(e => console.error(e));
  },
  // Given a number of files to fit, will select and apply the correct template for the current page.
  selectTemplateToFitImages: (nOfFiles, orientationTag) => {
    if (nOfFiles < 1) return;

    const activeSection = getCurrentSection();
    if (!activeSection) return; // Return if we are unable to select the correct section for getting the right templates

    const template = TemplateStore.getTemplateByPhotoSlots(
      nOfFiles,
      activeSection,
      orientationTag
    );

    // Fire an event to apply the chosen template
    useTemplateSelectStore
      .getState()
      .updateTemplateSelectStore({ templateID: template.id });
  },
  updatePrismLoaded: () => set(() => ({ prismItemLoaded: true })),
  updateId: id =>
    set(({ prismItem }) => ({ prismItem: { ...prismItem, id: id } })),
  updateLoadActive: loadActive =>
    set(({ prismItem }) => ({
      prismItem: { ...prismItem, loadActive: loadActive },
    })),
  updateImageList: imageList => {
    set(({ prismItem }) => ({
      prismItem: { ...prismItem, imageList: imageList },
    }));
  },
  updatePrismItem: props =>
    set(({ prismItem }) => ({ prismItem: { ...prismItem, ...props } })),

}));
